import React from 'react';
import KmoCta from '../KmoCta';
import MkIcon from '../MkIcon';
import MkPhone from '../MkPhone';
import MkPill from '../MkPill';
import MkPlanTable from '../MkPlanTable';
import MkReviewCard from '../MkReviewCard';
import kmoSchedule from '../../../assets/images/mk/mk-kmo-schedule-510x740.webp';
import { REVIEWS } from '../../../marketing/reviews';

const kmoLogo = require('../../../assets/images/logo-icon.svg').default;

const THEME_DOTS = ['#0B1220', '#1E5AA8', '#F08A4B', '#2E7D5B', '#D4A640'];

// Order = source order = the ≤640 single-column order.
const CARDS = [
  {
    area: 'quick',
    icon: 'lock',
    title: 'Quick Lock',
    body: 'Dial in hours and minutes, tap Lock, and your phone stays locked until the time is up.',
    review: REVIEWS.reem,
  },
  {
    area: 'sched',
    icon: 'calendar',
    title: 'Schedule Lock',
    body: 'Lock automatically for bedtime, study time or work hours, on the days you choose.',
    pills: [
      { variant: 'free', text: 'Free: 1 schedule' },
      { variant: 'premium', text: 'Premium: unlimited' },
    ],
    review: REVIEWS.ghislaine,
  },
  {
    area: 'allow',
    icon: 'apps-check',
    title: 'Allowed Apps',
    body: 'Choose apps that stay usable while your phone is locked.',
    pills: [
      { variant: 'new', text: 'Now available' },
      { variant: 'free', text: 'Free: 1 app' },
      { variant: 'premium', text: 'Premium: up to 15' },
    ],
    micro: 'Needs Usage access and notifications.',
    review: REVIEWS.anshu,
  },
  {
    area: 'place',
    icon: 'pin',
    title: 'Location Locks',
    body: 'Lock automatically when you arrive at a place you save. Your location is used only on your device — never uploaded or shared.',
    pills: [{ variant: 'premium', text: 'Premium' }],
  },
  {
    area: 'call',
    icon: 'phone',
    title: 'Emergency Calling',
    body: 'Turn it on in Settings to call the contacts you choose during a lock.',
    micro: 'Allow Usage access so calls work during a lock.',
  },
  {
    area: 'usage',
    icon: 'chart',
    title: 'App Usage',
    body: 'See where your time goes, starting with your last 7 days.',
  },
  {
    area: 'widget',
    icon: 'widget',
    title: 'Home-screen widgets',
    body: 'A focus dial, app usage and a weekly-trend chart, right on your home screen.',
  },
  {
    area: 'cheat',
    icon: 'shield-lock',
    title: 'Built to hold on bad days',
    body: 'Switch on "Keep locked after a restart" in Settings, and uninstall protection adds a deliberate extra step before the app can be removed. Hard to cheat on impulse.',
  },
  {
    area: 'theme',
    icon: 'palette',
    title: 'Themes',
    body: 'Colour themes like Midnight, Ocean, Sunset, Forest and Gold. Some unlock with Premium or a rewarded ad.',
  },
];

const PLAN_ROWS = [
  { feature: 'Schedule locks', free: '1', premium: 'Unlimited' },
  { feature: 'Allowed apps', free: '1', premium: 'Up to 15' },
  { feature: 'Location Locks', free: false, premium: true },
];

const Medallion = () => (
  <div className="mk-medallion" aria-hidden="true">
    <span className="mk-medallion__ring mk-medallion__ring--1" />
    <span className="mk-medallion__ring mk-medallion__ring--2" />
    <span className="mk-medallion__ring mk-medallion__ring--3" />
    <span className="mk-medallion__disc" />
    <img className="mk-medallion__logo" src={kmoLogo} alt="" width={72} height={72} />
  </div>
);

// H4 — the Keep Me Out features that are live today, plus what Premium adds.
const HomeBento = ({ platform }) => (
  <section className="mk-bento section" id="features" aria-labelledby="mk-bento-title">
    <div className="container-sm">
      <div className="mk-head mk-bento__head reveal-from-bottom">
        <p className="mk-eyebrow">Keep Me Out · Android</p>
        <h2 id="mk-bento-title" className="mk-h2">
          Built to keep you out
        </h2>
        <p className="mk-lead">
          Lock now, lock on a schedule, or lock when you arrive somewhere — and keep what truly matters within reach.
        </p>
      </div>
    </div>
    <div className="container">
      <div className="mk-bento__grid">
        {CARDS.map((c, i) => (
          <div
            key={c.area}
            className={`mk-bento__cell mk-bento__cell--${c.area} reveal-from-bottom`}
            data-reveal-container=".mk-bento__grid"
            data-reveal-delay={i * 60}
          >
            <article
              className={`mk-card mk-card--hover mk-bento__card mk-bento__card--${c.area}`}
              aria-labelledby={`mk-bento-${c.area}`}
            >
              {c.area === 'quick' && <Medallion />}
              {c.area === 'cheat' && (
                <span className="mk-bento__deco mk-bento__deco--cheat" aria-hidden="true">
                  <MkIcon name="shield-lock" />
                </span>
              )}
              {c.area === 'place' && <span className="mk-bento__deco mk-bento__deco--place" aria-hidden="true" />}
              <div className="mk-bento__content">
                <span className={`mk-icon-tile${c.area === 'place' ? ' mk-icon-tile--glow' : ''}`}>
                  <MkIcon name={c.icon} />
                </span>
                <h3 id={`mk-bento-${c.area}`} className="mk-h3">
                  {c.title}
                </h3>
                <p className="mk-body">{c.body}</p>
                {c.pills && (
                  <p className="mk-pills">
                    {c.pills.map((p) => (
                      <MkPill key={p.text} variant={p.variant}>
                        {p.text}
                      </MkPill>
                    ))}
                  </p>
                )}
                {c.micro && <p className="mk-micro mk-bento__micro">{c.micro}</p>}
                {c.area === 'theme' && (
                  <span className="mk-dots" aria-hidden="true">
                    {THEME_DOTS.map((d) => (
                      <span key={d} className="mk-dots__dot" style={{ background: d }} />
                    ))}
                  </span>
                )}
                {c.review && <MkReviewCard review={c.review} variant="compact" className="mk-review--inset" />}
              </div>
              {c.area === 'sched' && (
                <div className="mk-bento__phone">
                  <div className="mk-float">
                    <MkPhone
                      src={kmoSchedule}
                      width={510}
                      height={740}
                      alt="Keep Me Out Schedule tab with a Focus lock from 09:00 AM to 05:00 PM"
                    />
                  </div>
                </div>
              )}
            </article>
          </div>
        ))}
      </div>
    </div>
    <div className="container-sm">
      <div className="mk-bento__planwrap section-inner">
        <div className="mk-card mk-planpanel reveal-scale-up">
          <h3 className="mk-h3">What Premium adds</h3>
          <MkPlanTable caption="Keep Me Out free and Premium" rows={PLAN_ROWS} />
          <p className="mk-micro mk-planpanel__note">
            Keep Me Out is free to download and contains ads. Premium is an optional in-app purchase.
          </p>
          <KmoCta placement="home_plan" platform={platform} showMeta={false} className="mk-planpanel__cta" />
        </div>
      </div>
    </div>
  </section>
);

export default HomeBento;
